// src/pages/FundPlanner/components/Portfolios/usePortfolioReportPositions.js
import { useCallback, useEffect, useMemo, useState } from "react";
import { useProfile } from "context/ProfileContext";
import {
  latestReportDateIso,
  loadPortfolioReportPositions,
  trimToEmpty,
} from "./portfolioReportPositions";

export default function usePortfolioReportPositions(reportScope, options = {}) {
  const { selectedOrg } = useProfile();
  const enabled = options.enabled !== false;
  const suppliedData = options.data || null;

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [data, setData] = useState({
    portfolios: [],
    funds: [],
    positions: [],
    rows: [],
  });

  const orgId = selectedOrg?.id || null;
  const asOfDate = trimToEmpty(reportScope?.asOfDate) || latestReportDateIso();
  const reportMode = reportScope?.reportMode === "period" ? "period" : "single";
  const startDate = trimToEmpty(reportScope?.startDate);
  const endDate = trimToEmpty(reportScope?.endDate) || asOfDate;
  const includeAllPortfolioGroups = Boolean(reportScope?.includeAllPortfolioGroups);
  const includedPortfolioIds = useMemo(
    () =>
      Array.isArray(reportScope?.includedPortfolioIds)
        ? reportScope.includedPortfolioIds
        : [],
    [reportScope?.includedPortfolioIds]
  );
  const includedFundIds = useMemo(
    () =>
      Array.isArray(reportScope?.includedFundIds)
        ? reportScope.includedFundIds
        : [],
    [reportScope?.includedFundIds]
  );

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");

    try {
      const result = await loadPortfolioReportPositions({
        orgId,
        asOfDate,
        reportMode,
        startDate,
        endDate,
        includeAllPortfolioGroups,
        includedPortfolioIds,
        includedFundIds,
      });

      setData(result);
      setIsLoading(false);
    } catch (err) {
      setData({
        portfolios: [],
        funds: [],
        positions: [],
        rows: [],
      });
      setLoadError(err?.message || "Unable to load report positions.");
      setIsLoading(false);
    }
  }, [
    orgId,
    asOfDate,
    reportMode,
    startDate,
    endDate,
    includeAllPortfolioGroups,
    includedPortfolioIds,
    includedFundIds,
  ]);

  useEffect(() => {
    if (enabled) loadData();
  }, [enabled, loadData]);

  const resolvedData = suppliedData || data;

  return {
    isLoading: suppliedData ? Boolean(options.isLoading) : isLoading,
    loadError: suppliedData ? options.loadError || "" : loadError,
    asOfDate,
    portfolios: resolvedData.portfolios || [],
    funds: resolvedData.funds || [],
    positions: resolvedData.positions || [],
    rows: resolvedData.rows || [],
    reportMode,
    startDate,
    endDate,
    periodAnalysis: resolvedData.periodAnalysis || null,
    reload: loadData,
  };
}
